import classNames from 'classnames';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/Header/Header';
import TableCard from '@/components/Tables/TableCard/TableCard';
import Tabs from '@/components/Tabs/Tabs';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useExistsActiveFreezes } from '@/services/freeze/freeze.service';
import { useStores } from '@/services/stores/stores.service';
import { Store } from '@/types/stores.types';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import StoreGroups from './BundleGroups/StoreGroups';
import styles from './MyStores.module.css';

const MyStores: FC = () => {
  const { t } = useTranslation();
  const { alias } = useFranchiseeAlias();
  const { data: freezeExists } = useExistsActiveFreezes();
  const [activeTab, setActiveTab] = useState(0);
  const handleTabClick = (indexOfClickedTab: number) => {
    setActiveTab(indexOfClickedTab);
  };

  const { data, isLoading, isError } = useStores(true);
  const storeData = data?.data || [];

  if (isError) {
    showToast({ toastType: ToastTypes.Error, label: t('error.fetchingStoreData'), id: 'storeFetchError' });
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return <StoreGroups stores={storeData} storesIsLoading={isLoading} />;
      default:
        return (
          <TableCard
            className={classNames(styles.storesTable, styles.cardWidth)}
            includeMinWidth={false}
            enableFilters
            returnedRecordsText={(count) => t('myStores.table.returnedRecord', { count })}
            isLoading={isLoading}
            data={storeData}
            columns={[
              { accessorKey: 'storeNo', header: t('myStores.table.storeId') },
              {
                header: t('myStores.table.address'),
                accessorFn: (row: Store) => `${row.address}, ${row.city} ${row.state} ${row.zip}`,
              },
              { header: t('myStores.table.storeGroup'), accessorFn: (row: Store) => row.groupName || '-' },
            ]}
          />
        );
    }
  };

  return (
    <>
      <Header
        title={t('myStores.title')}
        breadcrumb={{ to: '/account/profile', label: t('myStores.breadcrumb') }}
        includeLogOut
      />
      <div
        className={classNames(styles.page, { [styles.withAlias]: alias !== null, [styles.withFreeze]: freezeExists })}
      >
        <Tabs
          activeTab={activeTab}
          onTabClick={handleTabClick}
          tabs={[
            { label: t('myStores.tabs.allStores'), id: 'stores' },
            { label: t('myStores.tabs.storeGroups'), id: 'groups' },
          ]}
        />
        {renderTabContent()}
      </div>
    </>
  );
};

export default MyStores;
